import type { OutreachRow } from '@discovr/types';
import { fmtRate } from './QuoteBlock';

type Agreed = NonNullable<OutreachRow['agreedDeliverables']>[number];

/** Rough INR per unit, for prefilling the Agree rate dialog only. Approximate by design (owner 2026-10-21): the admin confirms or edits the figure; no exchange-rate service. */
export const APPROX_INR_PER_UNIT: Readonly<Record<string, number>> = {
  USD: 88,
  EUR: 102,
  GBP: 117,
  AED: 24,
  AUD: 58,
  CAD: 63,
  SGD: 68,
};

/** Mirrors the backend cap on a rate (agent.MaxRateAmount). */
export const MAX_RATE_AMOUNT = 100_000_000;

/** Mirrors the backend 2-decimal rule (hasMoreThanTwoDecimals in negotiation/rounds.go). */
export const MAX_AMOUNT_DECIMALS = 2;
const SMALLEST_AMOUNT = 10 ** -MAX_AMOUNT_DECIMALS;

export const AMOUNT_DECIMAL_SEPARATOR = '.';
export const AMOUNT_GROUP_SEPARATOR = ',';
/** en-US groups every 3 digits; en-IN groups the last 3, then every 2 (lakh, crore). */
export const THOUSANDS_GROUP_DIGITS = 3;
export const LAKH_GROUP_DIGITS = 2;

const LAKH_GROUPING_CURRENCY = 'INR';
const WHOLE_DIGITS = /^\d+$/;
const INVALID_NUMBER_ERROR = 'Enter a valid number.';
const FIXED_UNGROUPED = new Intl.NumberFormat('en-US', {
  useGrouping: false,
  minimumFractionDigits: MAX_AMOUNT_DECIMALS,
  maximumFractionDigits: MAX_AMOUNT_DECIMALS,
});

const RUPEES_PER_CRORE = 10_000_000;

/** The cap in the row currency's own money style: "₹10 Cr" for INR, "$100,000,000" for USD. */
function fmtRateLimit(currency: string): string {
  const code = currency.toUpperCase();
  if (code === LAKH_GROUPING_CURRENCY) return `₹${MAX_RATE_AMOUNT / RUPEES_PER_CRORE} Cr`;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(MAX_RATE_AMOUNT);
  } catch {
    return `${code} ${MAX_RATE_AMOUNT.toLocaleString('en-US')}`;
  }
}

/** Why a typed rate can't be sent, or null when it can. Zero is "not typed yet" and is handled by the caller. */
export function rateAmountError(amount: number, currency: string): string | null {
  if (Number.isNaN(amount)) return INVALID_NUMBER_ERROR;
  // Before the finite check, so an overlong paste that reads as Infinity still explains the cap.
  if (amount > MAX_RATE_AMOUNT) return `Amount can't be more than ${fmtRateLimit(currency)}.`;
  if (!Number.isFinite(amount)) return INVALID_NUMBER_ERROR;
  if (amount <= 0) return 'Amount must be greater than 0.';
  const s = String(amount);
  const dot = s.indexOf(AMOUNT_DECIMAL_SEPARATOR);
  if (amount < SMALLEST_AMOUNT || (dot >= 0 && s.length - dot - 1 > MAX_AMOUNT_DECIMALS)) {
    return `Use at most ${MAX_AMOUNT_DECIMALS} decimal places.`;
  }
  return null;
}

/**
 * Groups a whole-number digit string: lakh grouping for INR ("1,50,000"), thousands otherwise ("150,000").
 * Works on the string, so long amounts stay exact and leading zeros a deletion exposes stay put; other text comes back unchanged.
 */
export function groupAmountDigits(wholeDigits: string, currency: string): string {
  if (!WHOLE_DIGITS.test(wholeDigits)) return wholeDigits;
  const laterGroup = currency.toUpperCase() === LAKH_GROUPING_CURRENCY ? LAKH_GROUP_DIGITS : THOUSANDS_GROUP_DIGITS;
  return groupFromRight(wholeDigits, THOUSANDS_GROUP_DIGITS, laterGroup);
}

function groupFromRight(digits: string, firstGroup: number, laterGroup: number): string {
  const groups = [digits.slice(-firstGroup)];
  for (let end = digits.length - firstGroup; end > 0; end -= laterGroup) {
    groups.unshift(digits.slice(Math.max(0, end - laterGroup), end));
  }
  return groups.join(AMOUNT_GROUP_SEPARATOR);
}

export function approxInr(amount: number, currency: string): number | null {
  const rate = APPROX_INR_PER_UNIT[currency.toUpperCase()];
  return rate && Number.isFinite(amount) ? Math.round(amount * rate * 100) / 100 : null;
}

/** An amount as the masked input prefills it: "36,000.00", or "1,50,000.00" for INR. */
export function fmtAmountInput(amount: number, currency: string): string {
  if (!Number.isFinite(amount)) return '';
  const [whole = '', fraction = ''] = FIXED_UNGROUPED.format(amount).split(AMOUNT_DECIMAL_SEPARATOR);
  return `${groupAmountDigits(whole, currency)}${AMOUNT_DECIMAL_SEPARATOR}${fraction}`;
}

/** The creator's own figure as secondary text: "USD 500.00". */
export function fmtOriginal(amount: number, currency: string): string {
  return `${currency.toUpperCase()} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "₹41,500.00 (USD 500.00)": the agreed figure with the creator's original as secondary text. */
export function fmtAgreedMoney(
  amount: number,
  original?: { amount: number; currency: string },
  currency = 'INR',
): string {
  return original
    ? `${fmtRate(amount, currency)} (${fmtOriginal(original.amount, original.currency)})`
    : fmtRate(amount, currency);
}

export function fmtAgreedItem(d: Agreed): string {
  return fmtAgreedMoney(d.basePrice, originalOf(d), d.currency ?? 'INR');
}

function originalOf(d: Agreed): { amount: number; currency: string } | undefined {
  return d.originalAmount && d.originalCurrency
    ? { amount: d.originalAmount, currency: d.originalCurrency }
    : undefined;
}

/** The summed INR figure; the original shows as secondary only when every line carries one in the same currency. */
export function fmtAgreedTotal(items: Agreed[]): string {
  const total = items.reduce((sum, d) => sum + d.basePrice, 0);
  const originals = items.map(originalOf);
  const first = originals[0];
  const uniform = !!first && originals.every((o) => o && o.currency === first.currency);
  return fmtAgreedMoney(
    total,
    uniform
      ? {
          amount: originals.reduce((sum, o) => sum + (o?.amount ?? 0), 0),
          currency: first.currency,
        }
      : undefined,
    items[0]?.currency ?? 'INR',
  );
}
